import React, { useState, useEffect, useRef } from 'react';
import {
  Sun,
  Moon,
  Monitor,
  CaseSensitive,
  List,
  Brain,
  Plus,
  Upload,
  ArrowLeft,
  UserRound,
  Gauge,
  Heart,
  Settings as SettingsIcon,
  CreditCard,
  LogOut,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { FONT_OPTIONS, useFont } from '../../contexts/FontContext';
import { colorThemeOptions, colorThemes } from '../../themes/colors';
import Select from 'react-select';
import {
  createMemory,
  deleteMemory,
  getMemorySettings,
  importExternalMemories,
  listMemories,
  saveProfileMemories,
  startMemoryBackfillInBackground,
  updateMemorySettings,
} from '../../utils/memoryApi';
import { chunkConversationsForImport, parseExternalMemoryExport } from '../../utils/memoryImport';

import { currentPlan, PanelHeading, ProfilePanel, UsagePanel, SubscriptionPanel, GivingPanel } from './AccountPanels';
import { useAccountSettings } from './useAccountSettings';
import MemoryBrowser from './MemoryBrowser';
import LogoutConfirmation from './LogoutConfirmation';
import './Settings.css';

const noop = () => {};

const Settings = ({
  hasSubscription,
  hasBasicSubscription = false,
  hasMaxSubscription = false,
  subscriptionStatus,
  isCheckingSubscription = false,
  onRefreshSubscription = noop,
  subscriptionSources = [],
  onLogout = noop,
  onUpgrade = noop,
  onBackToChat = noop,
  particlesEnabled = false,
  setParticlesEnabled = noop,
  chatSidebarViewMode = 'concise',
  setChatSidebarViewMode = noop,
}) => {
  const [activeSection, setActiveSection] = useState('general');
  const [isConfirmingLogout, setIsConfirmingLogout] = useState(false);
  const logoutButtonRef = useRef(null);
  const panelRef = useRef(null);
  const account = useAccountSettings(activeSection);
  const plan = currentPlan({ hasSubscription, hasBasicSubscription, hasMaxSubscription, subscriptionStatus, isCheckingSubscription });
  useEffect(() => { if (panelRef.current) panelRef.current.scrollTop = 0; }, [activeSection]);
  const {
    theme,
    setTheme,
    colorTheme,
    setColorTheme,
    glowEnabled,
    setGlowEnabled,
    glowColor,
    setGlowColor
  } = useTheme();
  const { currentFont, setCurrentFont } = useFont();
  const [isLoading, setIsLoading] = useState(false);
  const [memories, setMemories] = useState([]);
  const [deletingMemoryId, setDeletingMemoryId] = useState(null);
  const [memoryInput, setMemoryInput] = useState('');
  const [memoryError, setMemoryError] = useState('');
  const [isSavingMemory, setIsSavingMemory] = useState(false);
  const [memoryEnabled, setMemoryEnabled] = useState(false);
  const [memorySettings, setMemorySettings] = useState(null);
  const [importSource, setImportSource] = useState('auto');
  const [isImportingMemories, setIsImportingMemories] = useState(false);
  const [memoryImportProgress, setMemoryImportProgress] = useState(null);
  const [aboutNickname, setAboutNickname] = useState('');
  const [aboutOccupation, setAboutOccupation] = useState('');
  const [isSavingProfileMemories, setIsSavingProfileMemories] = useState(false);
  const savedMemoriesEnabled = memorySettings?.savedMemoriesEnabled ?? memoryEnabled;
  const chatHistoryEnabled = memorySettings?.chatHistoryEnabled ?? memoryEnabled;
  const aboutMeHasChanges =
    aboutNickname.trim() !== (memorySettings?.nickname || '').trim() ||
    aboutOccupation.trim() !== (memorySettings?.occupation || '').trim() ||
    memoryInput.trim().length > 0;
  const backfillChatsScanned = Number(memorySettings?.backfillChatsScanned) || 0;
  const backfillChatsTotal = Number(memorySettings?.backfillChatsTotal) || 0;
  const backfillMessagesScanned = Number(memorySettings?.backfillMessagesScanned) || 0;
  const backfillProgressPercent =
    memorySettings?.backfillStatus === 'completed'
      ? 100
      : backfillChatsTotal > 0
      ? Math.min(100, Math.round((backfillChatsScanned / backfillChatsTotal) * 100))
      : 0;
  const backfillProgressWidth =
    memorySettings?.backfillStatus === 'completed'
      ? 100
      : backfillChatsTotal > 0
        ? Math.min(100, Math.max(2, (backfillChatsScanned / backfillChatsTotal) * 100))
        : 0;

  const fontOptions = FONT_OPTIONS.map((font) => ({
    value: font.name,
    label: font.name,
    fontFamily: font.fontFamily,
  }));

  // Conditional styling for dark vs light mode
  // Removed unused isDark variable
  const isDarkMode = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  const darkBg = '#242424';
  const darkBgHover = '#1e1e1e';
  const lightBg = '#FFFFFF';
  const lightBgHover = '#F2F2F2';
  const darkText = '#CBD5E0';
  const lightText = '#4A5568';
  const settingsBorderColor = 'var(--settings-border-color, var(--color-border-subtle))';
  const settingsControlBorderColor = 'var(--settings-control-border-color, var(--color-border-strong))';

  const customStyles = {
    // The container that wraps the control
    container: (provided) => ({
      ...provided,
      backgroundColor: isDarkMode ? darkBg : lightBg,
      color: isDarkMode ? darkText : lightText,
    }),
    // The control is the clickable area that shows the current value
    control: (provided, state) => ({
      ...provided,
      backgroundColor: isDarkMode ? darkBg : lightBg,
      borderColor: settingsBorderColor,
      color: isDarkMode ? darkText : lightText,
      ':hover': {
        borderColor: settingsControlBorderColor,
      },
      boxShadow: state.isFocused ? `0 0 0 1px ${settingsControlBorderColor}` : null,
    }),
    // The dropdown menu
    menu: (provided) => ({
      ...provided,
      backgroundColor: isDarkMode ? darkBg : lightBg,
      zIndex: 9999,
    }),
    // Individual options in the list
    option: (provided, state) => ({
      ...provided,
      fontFamily: state.data.fontFamily,
      backgroundColor: state.isFocused
        ? isDarkMode ? darkBgHover : lightBgHover
        : isDarkMode ? darkBg : lightBg,
      color: isDarkMode ? darkText : lightText,
      cursor: 'pointer'
    }),
    // The currently selected value
    singleValue: (provided, state) => ({
      ...provided,
      fontFamily: state.data.fontFamily,
      color: isDarkMode ? darkText : lightText,
    }),
    // The placeholder text
    placeholder: (provided) => ({
      ...provided,
      color: isDarkMode ? '#718096' : '#A0AEC0',
    }),
    // The indicators (dropdown arrow, clear button, etc.)
    dropdownIndicator: (provided, state) => ({
      ...provided,
      color: isDarkMode ? darkText : lightText,
      ':hover': {
        color: isDarkMode ? '#FFFFFF' : '#2D3748',
      },
    }),
    // The value container holding the selected value
    valueContainer: (provided) => ({
      ...provided,
      backgroundColor: isDarkMode ? darkBg : lightBg,
      color: isDarkMode ? darkText : lightText,
    }),
  };

  useEffect(() => {
    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => {
        document.documentElement.classList.toggle('dark', mediaQuery.matches);
      };
      mediaQuery.addListener(handleChange);
      handleChange();
      return () => mediaQuery.removeListener(handleChange);
    }
  }, [theme]);

  useEffect(() => {
    if (activeSection !== 'memory') return;

    let isMounted = true;
    setIsLoading(true);
    setMemories([]);
    setMemorySettings(null);
    setMemoryEnabled(false);
    setAboutNickname('');
    setAboutOccupation('');
    setMemoryError('');
    Promise.all([getMemorySettings(), listMemories({ type: 'all' })])
      .then(([settingsPayload, memoriesPayload]) => {
        if (isMounted) {
          setMemorySettings(settingsPayload.settings || null);
          setMemoryEnabled(settingsPayload.settings?.enabled === true);
          setAboutNickname(settingsPayload.settings?.nickname || '');
          setAboutOccupation(settingsPayload.settings?.occupation || '');
          setMemories(memoriesPayload.memories || []);
        }
      })
      .catch((error) => {
        if (isMounted) {
          setMemoryError(error.message || 'Failed to load memories');
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [activeSection, account.user?.uid]);

  useEffect(() => {
    if (activeSection !== 'memory') return;
    if (!['queued', 'running', 'retrying'].includes(memorySettings?.backfillStatus)) return;

    const intervalId = setInterval(async () => {
      try {
        const payload = await getMemorySettings();
        setMemorySettings(payload.settings || null);
        if (payload.settings?.backfillStatus === 'completed') {
          const memoriesPayload = await listMemories({ type: 'all' });
          setMemories(memoriesPayload.memories || []);
        }
      } catch (error) {
        setMemoryError('Unable to refresh progress. Indexing can continue on the server.');
      }
    }, 2500);

    return () => clearInterval(intervalId);
  }, [activeSection, memorySettings?.backfillStatus]);

  const handleSaveMemory = async () => {
    const text = memoryInput.trim();
    if (!text) return;

    setIsSavingMemory(true);
    setMemoryError('');
    try {
      await createMemory(text);
      setMemoryInput('');
      const payload = await listMemories({ type: 'all' });
      setMemories(payload.memories || []);
    } catch (error) {
      setMemoryError(error.message || 'Failed to save memory');
    } finally {
      setIsSavingMemory(false);
    }
  };

  const handleSaveProfileMemories = async () => {
    setMemoryError('');
    setIsSavingProfileMemories(true);
    try {
      const payload = await saveProfileMemories({
        nickname: aboutNickname.trim(),
        occupation: aboutOccupation.trim(),
      });
      setMemorySettings(payload.settings || null);
      const memoriesPayload = await listMemories({ type: 'all' });
      setMemories(memoriesPayload.memories || []);
    } catch (error) {
      setMemoryError(error.message || 'Failed to save profile memories');
    } finally {
      setIsSavingProfileMemories(false);
    }
  };

  const handleSaveAboutMe = async () => {
    await handleSaveProfileMemories();
    if (memoryInput.trim()) {
      await handleSaveMemory();
    }
  };

  const updateMemoryPreference = async ({ nextSavedMemoriesEnabled, nextChatHistoryEnabled }) => {
    const enabled = nextSavedMemoriesEnabled || nextChatHistoryEnabled;
    setMemoryError('');
    try {
      const payload = await updateMemorySettings({
        enabled,
        savedMemoriesEnabled: nextSavedMemoriesEnabled,
        chatHistoryEnabled: nextChatHistoryEnabled,
      });
      setMemoryEnabled(payload.settings?.enabled === true);
      setMemorySettings(payload.settings || null);
      setMemories(prev => prev.filter(memory => memory.sourceType === 'explicit_memory'
        ? nextSavedMemoriesEnabled : nextChatHistoryEnabled));
      const memoriesPayload = await listMemories({ type: 'all' });
      setMemories(memoriesPayload.memories || []);

    } catch (error) {
      setMemoryError(error.message || 'Failed to update memory settings');
    }
  };

  const handleToggleSavedMemories = () => {
    updateMemoryPreference({
      nextSavedMemoriesEnabled: !savedMemoriesEnabled,
      nextChatHistoryEnabled: chatHistoryEnabled,
    });
  };

  const handleToggleChatHistory = () => {
    updateMemoryPreference({
      nextSavedMemoriesEnabled: savedMemoriesEnabled,
      nextChatHistoryEnabled: !chatHistoryEnabled,
    });
  };

  const handleRefreshMemories = async () => {
    setIsLoading(true);
    setMemoryError('');
    try {
      const [settingsPayload, memoriesPayload] = await Promise.all([
        getMemorySettings(), listMemories({ type: 'all' }),
      ]);
      setMemorySettings(settingsPayload.settings || null);
      setMemoryEnabled(settingsPayload.settings?.enabled === true);
      setAboutNickname(settingsPayload.settings?.nickname || '');
      setAboutOccupation(settingsPayload.settings?.occupation || '');
      setMemories(memoriesPayload.memories || []);
    } catch (error) {
      setMemoryError(error.message || 'Failed to load memories');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteMemory = async (memoryId) => {
    setMemoryError('');
    setDeletingMemoryId(memoryId);
    try {
      await deleteMemory(memoryId);
      const target = memories.find(memory => memory.id === memoryId);
      // Forgetting one learned item excludes its entire source chat on the server.
      setMemories(prev => prev.filter(memory => memory.id !== memoryId &&
        (!target?.chatId || memory.chatId !== target.chatId)));
      if (memoryId === 'profile_nickname') {
        setAboutNickname('');
        setMemorySettings(prev => ({ ...prev, nickname: '' }));
      } else if (memoryId === 'profile_occupation') {
        setAboutOccupation('');
        setMemorySettings(prev => ({ ...prev, occupation: '' }));
      }
      return true;
    } catch (error) {
      setMemoryError(error.message || 'Failed to delete memory');
      return false;
    } finally {
      setDeletingMemoryId(null);
    }
  };

  const handleImportMemoryFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setMemoryError('');
    setIsImportingMemories(true);
    setMemoryImportProgress({ imported: 0, total: 0, source: importSource });

    try {
      const rawText = await file.text();
      const parsedJson = JSON.parse(rawText);
      const { source, conversations } = parseExternalMemoryExport(parsedJson, importSource);
      const chunks = chunkConversationsForImport(conversations, 250);
      const totalMessages = conversations.reduce(
        (sum, conversation) => sum + conversation.messages.length,
        0
      );

      let imported = 0;
      setMemoryImportProgress({ imported, total: totalMessages, source });

      for (const chunk of chunks) {
        const summary = await importExternalMemories({
          source,
          conversations: chunk,
          maxMessages: 250,
        });
        imported += summary.messagesIndexed || 0;
        setMemoryImportProgress({ imported, total: totalMessages, source });
      }

      const [settingsPayload, memoriesPayload] = await Promise.all([
        getMemorySettings(),
        listMemories({ type: 'all' }),
      ]);
      setMemorySettings(settingsPayload.settings || null);
      setMemories(memoriesPayload.memories || []);
    } catch (error) {
      setMemoryError(error.message || 'Failed to import memories');
    } finally {
      setIsImportingMemories(false);
    }
  };

  const GeneralSection = () => (
    <div className="space-y-6">
      <PanelHeading title="General" />

      {/* Appearance Section */}
      <div>
        <h3 className="text-lg font-medium theme-text-primary mb-4">Appearance</h3>
        
        {/* Display Mode & Color Theme Card */}
        <div className="theme-card-bg rounded-lg p-4 mb-4">
          {/* Display Mode */}
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-3">
              <Sun size={16} className="theme-text-primary" />
              <span className="font-medium theme-text-primary">Display Mode</span>
            </div>
            <div className="flex gap-2">
              <button
                aria-pressed={theme === 'light'}
                onClick={() => setTheme('light')}
                className={`flex items-center px-3 py-2 text-sm font-medium rounded-full border transition-all ${
                  theme === 'light'
                    ? 'theme-button-primary border-2 text-white'
                    : 'theme-button-secondary theme-border theme-text-primary'
                }`}
              >
                <Sun className="mr-2" size={14} />
                Light
              </button>
              <button
                aria-pressed={theme === 'dark'}
                onClick={() => setTheme('dark')}
                className={`flex items-center px-3 py-2 text-sm font-medium rounded-full border transition-all ${
                  theme === 'dark'
                    ? 'theme-button-primary border-2 text-white'
                    : 'theme-button-secondary theme-border theme-text-primary'
                }`}
              >
                <Moon className="mr-2" size={14} />
                Dark
              </button>
              <button
                aria-pressed={theme === 'system'}
                onClick={() => setTheme('system')}
                className={`flex items-center px-3 py-2 text-sm font-medium rounded-full border transition-all ${
                  theme === 'system'
                    ? 'theme-button-primary border-2 text-white'
                    : 'theme-button-secondary theme-border theme-text-primary'
                }`}
              >
                <Monitor className="mr-2" size={14} />
                System
              </button>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t theme-border my-4"></div>

          {/* Color Theme */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-4 h-4 rounded-full theme-button-primary"></div>
              <span className="font-medium theme-text-primary">Color Theme</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {colorThemeOptions.map(({ id, label }) => (
                <button
                  key={id}
                  type="button"
                  className={`w-full px-4 py-3 rounded-lg border text-sm font-semibold transition-all ${
                    colorTheme === id
                      ? 'theme-button-primary text-white border-2'
                      : 'theme-button-secondary theme-border theme-text-primary'
                  }`}
                  onClick={() => setColorTheme(id)}
                  aria-pressed={colorTheme === id}
                >
                  <div className="flex flex-col items-center gap-1">
                    <div className="flex gap-1" aria-hidden="true">
                      <div
                        className="w-3 h-3 rounded-full border theme-border"
                        style={{ backgroundColor: colorThemes[id].light['app-background'] }}
                      ></div>
                      <div
                        className="w-3 h-3 rounded-full border theme-border"
                        style={{ backgroundColor: colorThemes[id].dark['app-background'] }}
                      ></div>
                    </div>
                    <span>{label}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Font Selection */}
      <div className="border-t theme-border pt-6">
        <div className="theme-card-bg rounded-lg p-4 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <CaseSensitive size={16} className="theme-text-primary" />
            <span className="font-medium theme-text-primary">Font Family</span>
          </div>
          <div className="max-w-xs">
            <Select
              inputId="settings-font"
              aria-label="Font family"
              value={fontOptions.find((opt) => opt.value === currentFont)}
              onChange={(selected) => setCurrentFont(selected.value)}
              options={fontOptions}
              styles={customStyles}
              classNamePrefix="font-select"
              menuPlacement="auto"
            />
          </div>
        </div>
      </div>

      {/* Chat List View Mode */}
      <div className="border-t theme-border pt-6">
        <div className="theme-card-bg rounded-lg p-4 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <List size={16} className="theme-text-primary" />
            <span className="font-medium theme-text-primary">Chat List View</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <button
              className={`w-full px-4 py-3 rounded-lg border text-sm font-semibold transition-all ${
                chatSidebarViewMode === 'concise' 
                  ? 'theme-button-primary text-white border-2'
                  : 'theme-button-secondary theme-border theme-text-primary'
              }`}
              aria-pressed={chatSidebarViewMode === 'concise'}
              onClick={() => setChatSidebarViewMode('concise')}
            >
              Concise
            </button>
            <button
              className={`w-full px-4 py-3 rounded-lg border text-sm font-semibold transition-all ${
                chatSidebarViewMode === 'detailed' 
                  ? 'theme-button-primary text-white border-2'
                  : 'theme-button-secondary theme-border theme-text-primary'
              }`}
              aria-pressed={chatSidebarViewMode === 'detailed'}
              onClick={() => setChatSidebarViewMode('detailed')}
            >
              Detailed
            </button>
          </div>
          <div className="text-xs theme-text-muted mt-2 text-center">
            {chatSidebarViewMode === 'detailed' ? 'Shows timestamps and backgrounds' : 'Shows titles only'}
          </div>
        </div>
      </div>

      {/* Visual Effects */}
      <div className="border-t theme-border pt-6">
        <h3 className="text-lg font-medium theme-text-primary mb-4">Visual Effects</h3>
        <div className="theme-card-bg rounded-lg p-4 mb-4">
          {/* Particle Background Toggle */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-gradient-to-br from-blue-400 to-purple-500"></div>
              <span className="font-medium theme-text-primary">Particle Background</span>
            </div>
            <button
              role="switch"
              aria-label="Particle background"
              aria-checked={particlesEnabled}
              onClick={() => setParticlesEnabled(!particlesEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                particlesEnabled ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  particlesEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Divider */}
          <div className="border-t theme-border my-4"></div>

          {/* Glow Effects Toggle */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div 
                className="w-4 h-4 rounded-full" 
                style={{ backgroundColor: glowColor }}
              ></div>
              <span className="font-medium theme-text-primary">Glow Effects</span>
            </div>
            <button
              role="switch"
              aria-label="Glow effects"
              aria-checked={glowEnabled}
              onClick={() => setGlowEnabled(!glowEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                glowEnabled ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  glowEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Glow Color Picker - Only show when glow is enabled */}
          {glowEnabled && (
            <div className="mt-4">
              <div className="text-xs font-medium theme-text-primary mb-2">Glow Color</div>
              <div className="flex gap-2 flex-wrap">
                {[
                  { color: '#3b82f6', name: 'Blue' },
                  { color: '#06b6d4', name: 'Cyan' },
                  { color: '#10b981', name: 'Green' },
                  { color: '#f59e0b', name: 'Amber' },
                  { color: '#ef4444', name: 'Red' },
                  { color: '#ec4899', name: 'Pink' }
                ].map(({ color, name }) => (
                  <button
                    key={color}
                    onClick={() => setGlowColor(color)}
                    className={`w-8 h-8 rounded-full border-2 transition-all ${
                      glowColor === color 
                        ? 'border-white dark:border-gray-200 ring-2 ring-offset-2 ring-offset-gray-100 dark:ring-offset-gray-800' 
                        : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                    }`}
                    style={{ backgroundColor: color }}
                    title={name}
                    aria-label={`${name} glow`}
                    aria-pressed={glowColor === color}
                  />
                ))}
                {/* Custom Color Picker Button */}
                <div className="relative">
                  <input
                    type="color"
                    value={glowColor}
                    onChange={(e) => setGlowColor(e.target.value)}
                    className="w-8 h-8 rounded-full border-2 border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 cursor-pointer opacity-0 absolute inset-0"
                    title="Custom Color"
                    aria-label="Custom glow color"
                  />
                  <div 
                    className="w-8 h-8 rounded-full border-2 border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 cursor-pointer flex items-center justify-center text-xs font-bold theme-text-primary"
                    style={{ background: 'conic-gradient(red, yellow, lime, aqua, blue, magenta, red)' }}
                  >
                    +
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

    </div>
  );

  const ToggleSwitch = ({ checked, onChange, label }) => (
    <button
      type="button"
      onClick={onChange}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
        checked ? 'theme-button-primary' : 'bg-gray-300 dark:bg-gray-600'
      }`}
      role="switch"
      aria-checked={checked}
      aria-label={label}
      title={label}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );

  const MemorySection = () => (
    <div className="mx-auto max-w-3xl space-y-5">
      <div className="flex items-center justify-between border-b theme-border pb-4">
        <div className="flex items-center gap-2">
          <h2 id="settings-heading-memory" className="text-2xl font-semibold theme-text-primary">Memory</h2>
          <Brain size={16} className="theme-text-muted" />
        </div>
        <div className="text-xs theme-text-muted">
          {!isLoading && `${memories.length} memories`}
        </div>
      </div>

      <section>
        <div className="divide-y theme-border border-b theme-border">
          <div className="flex items-center justify-between gap-4 py-4">
            <div>
              <div className="text-sm font-medium theme-text-primary">Reference saved memories</div>
              <div className="mt-1 text-xs theme-text-muted">
                Let Arrow save and use memories when responding.
              </div>
            </div>
            <ToggleSwitch
              checked={savedMemoriesEnabled}
              onChange={handleToggleSavedMemories}
              label="Reference saved memories"
            />
          </div>

          <div className="py-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="text-sm font-medium theme-text-primary">Reference chat history</div>
                <div className="mt-1 text-xs theme-text-muted">
                  Let Arrow reference previous conversations when responding.
                </div>
              </div>
              <ToggleSwitch
                checked={chatHistoryEnabled}
                onChange={handleToggleChatHistory}
                label="Reference chat history"
              />
            </div>
            {chatHistoryEnabled && (
              <div className="mt-3 flex items-center justify-between gap-3 rounded-lg theme-card-bg px-3 py-2">
                <div className="text-xs theme-text-muted">
                  {['queued', 'running', 'retrying'].includes(memorySettings?.backfillStatus)
                    ? (memorySettings?.backfillStatus === 'queued' ? 'Queued for indexing' : memorySettings?.backfillStatus === 'retrying' ? 'Retrying automatically' : 'Indexing on Arrow’s servers')
                    : memorySettings?.backfillStatus === 'completed'
                      ? 'Past chats indexed'
                      : memorySettings?.backfillStatus === 'failed' ? 'Indexing paused after repeated errors' : 'Ready to index past chats'}
                </div>
                <div className="text-right text-xs theme-text-muted">
                  {(['queued', 'running', 'retrying'].includes(memorySettings?.backfillStatus) || memorySettings?.backfillStatus === 'completed') &&
                  backfillChatsTotal > 0
                    ? `${backfillProgressPercent}%`
                    : `${backfillMessagesScanned.toLocaleString()} scanned`}
                </div>
              </div>
            )}
            {memorySettings?.backfillStatus === 'failed' && (
              <button type="button" className="mt-2 text-sm underline" onClick={async () => {
                setMemoryError('');
                await startMemoryBackfillInBackground({ onError: error => setMemoryError(error.message) });
                try { const payload = await getMemorySettings(); setMemorySettings(payload.settings); }
                catch (error) { setMemoryError(error.message); }
              }}>Retry indexing</button>
            )}
            {['queued', 'running', 'retrying'].includes(memorySettings?.backfillStatus) && (
              <>
                <div className="mt-2 h-1.5 overflow-hidden rounded-full border theme-border">
                  <div
                    className={`h-full theme-button-primary transition-all duration-500 ${
                      backfillChatsTotal > 0 ? '' : 'w-2/3 animate-pulse'
                    }`}
                    style={backfillChatsTotal > 0 ? { width: `${backfillProgressWidth}%` } : undefined}
                  />
                </div>
                <div className="mt-1 flex items-center justify-between text-[11px] theme-text-muted">
                  <span>
                    {backfillChatsTotal > 0
                      ? `${backfillChatsScanned.toLocaleString()} of ${backfillChatsTotal.toLocaleString()} chats`
                      : `${backfillMessagesScanned.toLocaleString()} messages scanned`}
                  </span>
                  {backfillChatsTotal > 0 && (
                    <span>{backfillMessagesScanned.toLocaleString()} messages scanned</span>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {false && (
        <div className="mt-5 rounded-lg theme-card-bg p-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-sm font-medium theme-text-primary">Import from ChatGPT or Claude</div>
              <div className="mt-1 text-xs theme-text-muted">
                Upload a JSON export to index those conversations into chat history.
              </div>
            </div>
            <select
              value={importSource}
              onChange={(event) => setImportSource(event.target.value)}
              disabled={!chatHistoryEnabled || isImportingMemories}
              className="rounded-lg border theme-border theme-panel-bg px-2 py-1.5 text-xs theme-text-primary disabled:opacity-60"
            >
              <option value="auto">Auto</option>
              <option value="chatgpt">ChatGPT</option>
              <option value="claude">Claude</option>
            </select>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <label
              className={`inline-flex items-center rounded-lg theme-button-secondary px-3 py-1.5 text-xs theme-text-primary ${
                !chatHistoryEnabled || isImportingMemories ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
              }`}
            >
              <Upload size={14} className="mr-1.5" />
              {isImportingMemories ? 'Importing...' : 'Choose export'}
              <input
                type="file"
                accept=".json,application/json"
                disabled={!chatHistoryEnabled || isImportingMemories}
                onChange={handleImportMemoryFile}
                className="hidden"
              />
            </label>
            {memoryImportProgress && (
              <div className="text-xs theme-text-muted">
                {memoryImportProgress.imported} of {memoryImportProgress.total} messages imported
              </div>
            )}
          </div>

          {isImportingMemories && (
            <div className="mt-3 h-1.5 overflow-hidden rounded-full border theme-border">
              <div className="h-full w-2/3 theme-button-primary animate-pulse" />
            </div>
          )}
        </div>
        )}
      </section>

      <section className="pt-3">
        <div className="mb-3 border-b theme-border pb-3">
          <h3 className="text-lg font-semibold theme-text-primary">About Me</h3>
        </div>

        <div className="space-y-4">
          <label className="block">
            <span className="mb-2 block text-sm font-medium theme-text-primary">Name</span>
            <input
              value={aboutNickname}
              onChange={(event) => setAboutNickname(event.target.value)}
              placeholder="What should Arrow call you?"
              disabled={!savedMemoriesEnabled || isSavingProfileMemories}
              className="w-full rounded-lg border theme-border theme-card-bg px-3 py-2.5 text-sm theme-text-primary placeholder:text-gray-400 focus:outline-none disabled:opacity-60"
            />
          </label>

          <label className="block">
            <span className="mb-2 block text-sm font-medium theme-text-primary">Occupation</span>
            <input
              value={aboutOccupation}
              onChange={(event) => setAboutOccupation(event.target.value)}
              placeholder="What do you do?"
              disabled={!savedMemoriesEnabled || isSavingProfileMemories}
              className="w-full rounded-lg border theme-border theme-card-bg px-3 py-2.5 text-sm theme-text-primary placeholder:text-gray-400 focus:outline-none disabled:opacity-60"
            />
          </label>

          <label className="block">
            <span className="mb-2 block text-sm font-medium theme-text-primary">More about you</span>
            <div className="flex gap-2">
              <input
                value={memoryInput}
                onChange={(event) => setMemoryInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleSaveMemory();
                  }
                }}
                placeholder="Interests, values, or preferences to keep in mind"
                disabled={!savedMemoriesEnabled}
                className="min-w-0 flex-1 rounded-lg border theme-border theme-card-bg px-3 py-2.5 text-sm theme-text-primary placeholder:text-gray-400 focus:outline-none disabled:opacity-60"
              />
            </div>
          </label>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleSaveAboutMe}
              disabled={
                !savedMemoriesEnabled ||
                !aboutMeHasChanges ||
                isSavingProfileMemories ||
                isSavingMemory
              }
              className={`inline-flex items-center rounded-lg px-3 py-2 text-sm font-medium disabled:opacity-50 ${
                aboutMeHasChanges
                  ? 'theme-button-primary text-white'
                  : 'theme-button-secondary theme-text-primary'
              }`}
            >
              <Plus size={15} className="mr-1.5" />
              {isSavingProfileMemories || isSavingMemory ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>

      </section>

      <MemoryBrowser
        key={account.user?.uid || 'guest'}
        memories={memories}
        isLoading={isLoading}
        error={memoryError}
        savedEnabled={savedMemoriesEnabled}
        historyEnabled={chatHistoryEnabled}
        onRefresh={handleRefreshMemories}
        onDelete={handleDeleteMemory}
        deletingId={deletingMemoryId}
      />
    </div>
  );

  const settingsSections = [
    { id: 'general', label: 'General', icon: SettingsIcon },
    { id: 'profile', label: 'Profile', icon: UserRound },
    { id: 'usage', label: 'Usage', icon: Gauge },
    { id: 'subscription', label: 'Subscription', icon: CreditCard },
    { id: 'memory', label: 'Memory', icon: Brain },
    { id: 'giving', label: 'Giving', icon: Heart },
  ];

  return (
    <div className="settings-layout theme-panel-bg theme-text-primary">
      <aside className="settings-sidebar" inert={isConfirmingLogout ? '' : undefined} aria-hidden={isConfirmingLogout || undefined}>
        <button type="button" onClick={onBackToChat} className="settings-back">
          <ArrowLeft size={17} aria-hidden="true" />Back to Chat
        </button>
        <h1>Settings</h1>
        <nav className="settings-navigation" aria-label="Settings sections">
          {settingsSections.map(({ id, label, icon: Icon }) => (
            <button key={id} type="button" onClick={() => setActiveSection(id)}
              aria-current={activeSection === id ? 'page' : undefined}
              aria-controls="settings-content">
              <Icon size={18} aria-hidden="true" /><span>{label}</span>
            </button>
          ))}
        </nav>
        <div className="settings-mobile-navigation">
          <label htmlFor="settings-section">Settings section</label>
          <select id="settings-section" value={activeSection} onChange={event => setActiveSection(event.target.value)}>
            {settingsSections.map(section => <option key={section.id} value={section.id}>{section.label}</option>)}
          </select>
        </div>
        <div className="settings-sidebar-footer">
          <button ref={logoutButtonRef} type="button" className="settings-logout" onClick={() => setIsConfirmingLogout(true)}>
            <LogOut size={18} aria-hidden="true" />Log out
          </button>
        </div>
      </aside>
      <main id="settings-content" ref={panelRef} className="settings-content theme-scrollbar" aria-labelledby={`settings-heading-${activeSection}`}
        inert={isConfirmingLogout ? '' : undefined} aria-hidden={isConfirmingLogout || undefined}>
        {activeSection === 'general' && GeneralSection()}
        {activeSection === 'profile' && <ProfilePanel account={account} plan={plan} />}
        {activeSection === 'usage' && <UsagePanel account={account} plan={plan} onSubscription={() => setActiveSection('subscription')} />}
        {activeSection === 'subscription' && <SubscriptionPanel plan={plan} sources={subscriptionSources} status={subscriptionStatus} checking={isCheckingSubscription} onRefresh={onRefreshSubscription} onUpgrade={onUpgrade} />}
        {activeSection === 'memory' && MemorySection()}
        {activeSection === 'giving' && <GivingPanel key={account.user?.uid || 'guest'} />}
      </main>
      {isConfirmingLogout && <LogoutConfirmation returnFocusRef={logoutButtonRef} onCancel={() => setIsConfirmingLogout(false)} onConfirm={onLogout} />}
    </div>
  );
};

export default Settings;
